/** Suggest a better translation (reader) and review suggestions (admin). */
import { useCallback, useEffect, useState } from 'react';
import type { MessageDTO } from '@tc/contracts';
import { api, ApiError } from '../api.ts';
import { languageName, useT, useUiLocale } from '../i18n.ts';
import { Dialog } from './Dialog.tsx';

export function SuggestDialog({ m, onClose }: { m: MessageDTO; onClose: () => void }) {
  const t = useT();
  const ui = useUiLocale();
  const [text, setText] = useState(m.translation?.body ?? '');
  const [comment, setComment] = useState('');
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');
  const submit = async () => {
    setState('busy');
    try {
      await api('POST', `/api/messages/${m.id}/suggestions`, { text, comment: comment || null });
      setState('done');
    } catch (err) {
      setState(err instanceof ApiError && err.code === 'unchanged' ? 'idle' : 'error');
    }
  };
  return (
    <Dialog title={t('suggestTitle')} onClose={onClose}>
      {state === 'done' ? (
        <div className="stack">
          <p>{t('suggestThanks')}</p>
          <button className="btn primary" onClick={onClose}>{t('close')}</button>
        </div>
      ) : (
        <div className="stack">
          <div className="field">
            <span className="muted small">{t('originalIn', { lang: languageName(ui, m.sourceLocale) })}</span>
            <blockquote className="suggest-src" lang={m.sourceLocale}>{m.body}</blockquote>
          </div>
          <label className="field">
            <span>{t('suggestYourVersion')}</span>
            <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} lang={m.translation?.targetLocale} />
          </label>
          <label className="field">
            <span>{t('suggestComment')}</span>
            <input value={comment} maxLength={500} onChange={(e) => setComment(e.target.value)} />
          </label>
          <p className="muted small">{t('suggestPrivacy')}</p>
          {state === 'error' && <p className="form-error" role="alert">{t('suggestFailed')}</p>}
          <div className="row">
            <button className="btn primary" disabled={state === 'busy' || !text.trim() || text.trim() === m.translation?.body?.trim()} onClick={() => void submit()}>
              {t('suggestSend')}
            </button>
            <button className="btn" onClick={onClose}>{t('cancel')}</button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

interface Suggestion {
  id: string;
  sourceLocale: string;
  targetLocale: string;
  sourceText: string;
  machineText: string;
  suggestedText: string;
  comment: string | null;
  suggestedBy: string;
  createdAt: string;
}

export function SuggestionsTab() {
  const t = useT();
  const ui = useUiLocale();
  const [items, setItems] = useState<Suggestion[] | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [apply, setApply] = useState(true);
  const load = useCallback(() => api<Suggestion[]>('GET', '/api/admin/suggestions?status=pending').then(setItems).catch(() => setItems([])), []);
  useEffect(() => void load(), [load]);
  const review = async (s: Suggestion, action: 'approve' | 'reject') => {
    await api('POST', `/api/admin/suggestions/${s.id}/review`, { action, text: edits[s.id] ?? s.suggestedText, applyToMessage: apply }).catch(() => {});
    await load();
  };
  if (!items) return <p className="muted">…</p>;
  return (
    <div className="stack">
      <p className="muted small">{t('suggestionsIntro')}</p>
      <label className="check">
        <input type="checkbox" checked={apply} onChange={(e) => setApply(e.target.checked)} />
        <span>{t('suggestApply')}</span>
      </label>
      {items.length === 0 && <p className="muted">{t('suggestionsEmpty')}</p>}
      {items.map((s) => (
        <article key={s.id} className="suggestion">
          <div className="sg-meta muted small">
            {s.suggestedBy} · {languageName(ui, s.sourceLocale)} → {languageName(ui, s.targetLocale)} · {new Date(s.createdAt).toLocaleDateString()}
          </div>
          <p className="sg-src" lang={s.sourceLocale}>{s.sourceText}</p>
          <p className="sg-machine" lang={s.targetLocale}><span className="muted small">{t('suggestMachine')}:</span> {s.machineText}</p>
          <label className="field">
            <span className="small">{t('suggestProposed')}</span>
            <textarea rows={2} lang={s.targetLocale} value={edits[s.id] ?? s.suggestedText} onChange={(e) => setEdits({ ...edits, [s.id]: e.target.value })} />
          </label>
          {s.comment && <p className="small"><span className="muted">{t('suggestComment')}:</span> {s.comment}</p>}
          <div className="row">
            <button className="btn small primary" onClick={() => void review(s, 'approve')}>{t('approve')}</button>
            <button className="btn small" onClick={() => void review(s, 'reject')}>{t('reject')}</button>
          </div>
        </article>
      ))}
    </div>
  );
}
